import ApiService from './api.service';

// BE resource REST = `purchase-orders` (module mua xe VinFast từ NPP).
// Response BE chuẩn hóa: { success, data, message }.
const BASE_URL = 'purchase-orders';

/**
 * PurchaseOrderService — wrapper quanh BE module "Đơn đặt xe".
 * BE controller: backend/src/modules/purchase-order/controllers/purchase-order.controller.ts
 *
 * Các action ghi state (approve/short-close/receive/return) là POST — có thể
 * truyền idempotency key (tham số thứ 3 của ApiService.post) để chống double-submit.
 */
export const PurchaseOrderService = {
  /**
   * List PO có filter + pagination.
   * @param {Object} params { status, providerId, page, limit }
   */
  getAll(params) {
    return ApiService.query(BASE_URL, { params });
  },

  /**
   * Detail PO (kèm items + provider).
   * @param {string} id
   */
  getById(id) {
    return ApiService.query(`${BASE_URL}/${id}`);
  },

  /**
   * Lịch sử thao tác PO (tạo/sửa/duyệt/nhận/thanh toán/đóng/hủy/trả).
   * Trả [{ id, action, userName, beforeValue, afterValue, createdAt }] (cũ→mới).
   * @param {string} id
   */
  getHistory(id) {
    return ApiService.query(`${BASE_URL}/${id}/history`);
  },

  /**
   * Toàn bộ chassis của PO (mọi trạng thái) + tên kho.
   * Trả [{ chassisId, chassisNumber, engineNumber, status, storeId, storeName,
   *        productCode, productName, expectedDeliveryDate,
   *        assignedReceiverBy, assignedReceiverName,
   *        confirmedByName, confirmedReceiptAt }]
   * @param {string} id
   */
  getPoChassis(id) {
    return ApiService.query(`${BASE_URL}/${id}/chassis`);
  },

  /**
   * Tạo PO nháp (DRAFT).
   * @param {Object} data CreatePurchaseOrderDto
   *   { providerId, expectedDeliveryDate?, paymentMethod?, bankName?,
   *     guaranteeNumber?, paymentAmount?, items:[{ productId, quantityOrdered, importPrice }] }
   */
  create(data) {
    return ApiService.post(BASE_URL, data);
  },

  /**
   * Sửa PO nháp (chỉ DRAFT) — PUT toàn bộ body.
   * @param {string} id
   * @param {Object} data UpdatePurchaseOrderDto (partial của Create)
   */
  update(id, data) {
    return ApiService.put(`${BASE_URL}/${id}`, data);
  },

  /**
   * Xóa PO (soft) — chỉ DRAFT.
   * @param {string} id
   */
  remove(id) {
    return ApiService.delete(`${BASE_URL}/${id}`);
  },

  /**
   * Hủy PO nháp (DRAFT → CANCELLED) — giữ bản ghi để truy vết.
   * @param {string} id
   * @param {string} [idempotentKey]
   */
  cancel(id, idempotentKey) {
    return ApiService.post(`${BASE_URL}/${id}/cancel`, {}, idempotentKey);
  },

  /**
   * Duyệt PO (DRAFT → APPROVED) + cộng committedDebt cho NPP.
   * @param {string} id
   * @param {string} [idempotentKey]
   */
  approve(id, idempotentKey) {
    return ApiService.post(`${BASE_URL}/${id}/approve`, {}, idempotentKey);
  },

  /**
   * Đóng ngắn PO (APPROVED → COMPLETED) — giải phóng committedDebt phần chưa nhập.
   * @param {string} id
   * @param {string} [reason]
   * @param {string} [idempotentKey]
   */
  shortClose(id, reason, idempotentKey) {
    return ApiService.post(
      `${BASE_URL}/${id}/short-close`,
      { reason },
      idempotentKey,
    );
  },

  /**
   * Preview import Excel số khung/số máy.
   * multipart field name = "file". Trả mảng row { row, chassisNumber,
   * engineNumber, purchaseOrderItemId, status(1=SUCCESS,0=ERROR), message }.
   * @param {string} id PO id
   * @param {FormData} formData (đã append 'file')
   */
  previewChassis(id, formData) {
    // ApiService.post → baseURL = READ_AND_WRITE_BACKEND; axios tự set
    // Content-Type multipart khi body là FormData.
    return ApiService.post(`${BASE_URL}/${id}/chassis/preview`, formData);
  },

  /**
   * Commit import — gửi lại các dòng SUCCESS.
   * @param {string} id PO id
   * @param {Array} rows các dòng status=SUCCESS từ preview
   * @param {string} [idempotentKey]
   */
  importChassis(id, rows, idempotentKey) {
    return ApiService.post(
      `${BASE_URL}/${id}/chassis/import`,
      { rows },
      idempotentKey,
    );
  },

  /**
   * Bổ sung số máy cho 1 chassis ảo.
   * @param {string} chassisId
   * @param {string} engineNumber
   */
  updateChassisEngine(chassisId, engineNumber) {
    return ApiService.patch(`${BASE_URL}/chassis/${chassisId}/engine`, {
      engineNumber,
    });
  },

  /**
   * Sửa số khung cho 1 chassis ảo (chỉ trạng thái PO_ORDERED — chưa ghép HĐ,
   * chưa xác nhận nhận kho). Dùng để sửa typo lúc nhập số khung chờ xe về.
   * @param {string} chassisId
   * @param {string} chassisNumber
   */
  updateChassisNumber(chassisId, chassisNumber) {
    return ApiService.patch(`${BASE_URL}/chassis/${chassisId}/chassis-number`, {
      chassisNumber,
    });
  },

  /**
   * Xác nhận đã nhận (1 bước): người duyệt nhận bấm xác nhận trực tiếp trên xe ảo
   * → ghi công nợ thực + tồn kho, chuyển xe PO_ORDERED/ON_HOLDING_PENDING_IMPORT
   * → NEW/ON_HOLDING.
   * @param {string} id PO id
   * @param {string[]} chassisIds danh sách chassis ảo cần xác nhận
   * @param {string} [idempotentKey]
   */
  confirmReceipt(id, chassisIds, idempotentKey) {
    return ApiService.post(
      `${BASE_URL}/${id}/confirm-receipt`,
      { chassisIds },
      idempotentKey,
    );
  },

  /**
   * Xuất trả NPP.
   * @param {Array} items [{ chassisId | chassisNumber }]
   * @param {string} [idempotentKey]
   */
  returnToSupplier(items, idempotentKey) {
    return ApiService.post(
      `${BASE_URL}/chassis/return-supplier`,
      { items },
      idempotentKey,
    );
  },

  /**
   * Báo cáo công nợ NPP (BR-008).
   * @param {Object} params { providerId? }
   */
  getSupplierDebtReport(params) {
    return ApiService.query(`${BASE_URL}/reports/supplier-debt`, { params });
  },

  /**
   * "Hàng đang về theo PO" (Edge 5.8).
   * @param {Object} params { poId? }
   */
  getPipelineReport(params) {
    return ApiService.query(`${BASE_URL}/reports/pipeline`, { params });
  },

  /**
   * Thanh toán công nợ NPP — giảm payableDebt (nợ thực tế).
   * @param {Object} data { providerId, purchaseOrderId?, amount, paymentDate?,
   *   paymentMethod?, note? }
   * @param {string} [idempotentKey]
   */
  payProviderDebt(data, idempotentKey) {
    return ApiService.post(
      `${BASE_URL}/provider-payments`,
      data,
      idempotentKey,
    );
  },

  /**
   * Lịch sử thanh toán công nợ NPP.
   * @param {Object} params { providerId?, purchaseOrderId? }
   */
  getProviderPayments(params) {
    return ApiService.query(`${BASE_URL}/provider-payments`, { params });
  },

  /**
   * PO có đáo hạn bảo lãnh/phong tỏa (Phase 3). FE tự tính badge sắp/quá hạn.
   * @param {Object} params { providerId? }
   */
  getGuaranteeDueReport(params) {
    return ApiService.query(`${BASE_URL}/reports/guarantee-due`, { params });
  },
};

export default PurchaseOrderService;
